import { createClient } from '@sanity/client'

// Main client for public reads (with CDN caching)
export const sanityClient = createClient({
  projectId: process.env.REACT_APP_SANITY_PROJECT_ID || 'agerv7pp',
  dataset: process.env.REACT_APP_SANITY_DATASET || 'production',
  token: process.env.REACT_APP_SANITY_TOKEN,
  useCdn: true,
  apiVersion: process.env.REACT_APP_SANITY_API_VERSION || '2024-01-01',
})

// Admin client without CDN (for real-time updates in admin panel)
export const sanityAdminClient = createClient({
  projectId: process.env.REACT_APP_SANITY_PROJECT_ID || 'agerv7pp',
  dataset: process.env.REACT_APP_SANITY_DATASET || 'production',
  token: process.env.REACT_APP_SANITY_TOKEN,
  useCdn: false,
  apiVersion: process.env.REACT_APP_SANITY_API_VERSION || '2024-01-01',
})

// Helper functions to fetch data
export const fetchTestimonials = async () => {
  const query = `*[_type == "testimonial"] | order(order asc) {
    _id,
    name,
    title,
    rating,
    text
  }`
  return await sanityAdminClient.fetch(query)
}

export const fetchGalleryImages = async () => {
  const query = `*[_type == "galleryImage"] | order(order asc) {
    _id,
    title,
    image {
      asset -> {
        url
      }
    },
    description
  }`
  return await sanityAdminClient.fetch(query)
}

export const fetchAbout = async () => {
  const query = `*[_type == "about"][0] {
    _id,
    title,
    image {
      asset -> {
        url
      }
    },
    bio
  }`
  return await sanityAdminClient.fetch(query)
}

export const fetchFooterContact = async () => {
  const query = `*[_type == "footerContact"][0] {
    _id,
    email,
    phone,
    address
  }`
  return await sanityAdminClient.fetch(query)
}

export const fetchAdminPassword = async () => {
  const query = `*[_type == "adminPassword"][0] {
    _id,
    password
  }`
  return await sanityAdminClient.fetch(query)
}

// ========== IMAGE UPLOAD ==========
export const uploadImage = async (file) => {
  try {
    // Validate file
    if (!file.type.startsWith('image/')) {
      throw new Error('Filen må være et bilde')
    }

    if (file.size > 5 * 1024 * 1024) {
      throw new Error('Bildet er for stort. Maks 5MB.')
    }

    const asset = await sanityAdminClient.assets.upload('image', file, {
      filename: file.name
    })

    return {
      _type: 'image',
      asset: {
        _type: 'reference',
        _ref: asset._id
      }
    }
  } catch (error) {
    console.error('Error uploading image:', error)
    throw error
  }
}

// ========== TESTIMONIALS CRUD ==========
export const createTestimonial = async (data) => {
  try {
    const existing = await fetchTestimonials()
    const maxOrder = existing && existing.length > 0 ? Math.max(...existing.map(t => t.order || 0)) : 0

    const testimonial = await sanityAdminClient.create({
      _type: 'testimonial',
      name: data.name,
      title: data.title,
      rating: parseInt(data.rating),
      text: data.text,
      order: maxOrder + 1
    })

    return testimonial
  } catch (error) {
    console.error('Error creating testimonial:', error)
    throw new Error('Kunne ikke opprette kundeomtale')
  }
}

export const updateTestimonial = async (id, data) => {
  try {
    const testimonial = await sanityAdminClient
      .patch(id)
      .set({
        name: data.name,
        title: data.title,
        rating: parseInt(data.rating),
        text: data.text
      })
      .commit()

    return testimonial
  } catch (error) {
    console.error('Error updating testimonial:', error)
    throw new Error('Kunne ikke oppdatere kundeomtale')
  }
}

export const deleteTestimonial = async (id) => {
  try {
    await sanityAdminClient.delete(id)
    return true
  } catch (error) {
    console.error('Error deleting testimonial:', error)
    throw new Error('Kunne ikke slette kundeomtale')
  }
}

export const reorderTestimonials = async (testimonials) => {
  try {
    const transaction = sanityAdminClient.transaction()

    testimonials.forEach((testimonial, index) => {
      transaction.patch(testimonial._id, p => p.set({ order: index }))
    })

    await transaction.commit()
    return true
  } catch (error) {
    console.error('Error reordering testimonials:', error)
    throw new Error('Kunne ikke endre rekkefølge')
  }
}

// ========== GALLERY CRUD ==========
export const createGalleryImage = async (data, imageFile) => {
  try {
    const imageRef = await uploadImage(imageFile)

    const existing = await fetchGalleryImages()
    const maxOrder = existing && existing.length > 0 ? Math.max(...existing.map(g => g.order || 0)) : 0

    const galleryImage = await sanityAdminClient.create({
      _type: 'galleryImage',
      title: data.title,
      description: data.description || '',
      image: imageRef,
      order: maxOrder + 1
    })

    return galleryImage
  } catch (error) {
    console.error('Error creating gallery image:', error)
    throw error.message || 'Kunne ikke laste opp bilde'
  }
}

export const updateGalleryImage = async (id, data, imageFile = null) => {
  try {
    let updateData = {
      title: data.title,
      description: data.description || ''
    }

    if (imageFile) {
      const imageRef = await uploadImage(imageFile)
      updateData.image = imageRef
    }

    const galleryImage = await sanityAdminClient
      .patch(id)
      .set(updateData)
      .commit()

    return galleryImage
  } catch (error) {
    console.error('Error updating gallery image:', error)
    throw error.message || 'Kunne ikke oppdatere bilde'
  }
}

export const deleteGalleryImage = async (id) => {
  try {
    await sanityAdminClient.delete(id)
    return true
  } catch (error) {
    console.error('Error deleting gallery image:', error)
    throw new Error('Kunne ikke slette bilde')
  }
}

export const reorderGalleryImages = async (images) => {
  try {
    const transaction = sanityAdminClient.transaction()

    images.forEach((image, index) => {
      transaction.patch(image._id, p => p.set({ order: index }))
    })

    await transaction.commit()
    return true
  } catch (error) {
    console.error('Error reordering gallery images:', error)
    throw new Error('Kunne ikke endre rekkefølge')
  }
}

// ========== ABOUT CRUD ==========
export const updateAbout = async (data, imageFile = null) => {
  try {
    const current = await fetchAbout()

    if (!current || !current._id) {
      // If about doesn't exist, create it
      let createData = {
        _type: 'about',
        title: data.title,
        bio: data.bio
      }

      if (imageFile) {
        const imageRef = await uploadImage(imageFile)
        createData.image = imageRef
      }

      return await sanityAdminClient.create(createData)
    }

    // Update existing
    let updateData = {
      title: data.title,
      bio: data.bio
    }

    if (imageFile) {
      const imageRef = await uploadImage(imageFile)
      updateData.image = imageRef
    }

    const about = await sanityAdminClient
      .patch(current._id)
      .set(updateData)
      .commit()

    return about
  } catch (error) {
    console.error('Error updating about:', error)
    throw error.message || 'Kunne ikke oppdatere om-seksjonen'
  }
}

// ========== FOOTER CONTACT CRUD ==========
export const updateFooterContact = async (data) => {
  try {
    const current = await fetchFooterContact()

    if (!current || !current._id) {
      // If contact doesn't exist, create it
      return await sanityAdminClient.create({
        _type: 'footerContact',
        email: data.email,
        phone: data.phone,
        address: data.address
      })
    }

    // Update existing
    const contact = await sanityAdminClient
      .patch(current._id)
      .set({
        email: data.email,
        phone: data.phone,
        address: data.address
      })
      .commit()

    return contact
  } catch (error) {
    console.error('Error updating footer contact:', error)
    throw new Error('Kunne ikke oppdatere kontaktinformasjon')
  }
}
